import { ChangeDetectionStrategy, Component, EventEmitter, OnDestroy, Output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AdminSupportListItemView, SupportStatus } from 'src/app/common/models/support/admin-support.models';
import { environment } from 'src/environments/environment';
import { OsloDatePipe } from 'src/app/pipes/oslo-date.pipe';

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-case-table',
  standalone: true,
  imports: [CommonModule, TranslateModule, OsloDatePipe],
  templateUrl: './case-table.component.html',
  styleUrls: ['./case-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaseTableComponent implements OnDestroy {
  isCaseNumberEnabled = environment.IS_SUPPORT_CASE_NUMBER_ENABLED;

  tab = input.required<SupportStatus>();
  cases = input.required<AdminSupportListItemView[]>();
  member = input<string | null>(null);
  page = input<number>(1);
  pageSize = input<number>(50);
  totalCount = input<number>(0);

  @Output() tabChanged = new EventEmitter<SupportStatus>();
  @Output() memberChanged = new EventEmitter<string | null>();
  @Output() rowClicked = new EventEmitter<string>();
  @Output() pageChanged = new EventEmitter<number>();

  private searchDebounceTimer?: ReturnType<typeof setTimeout>;

  onTabClick(tab: SupportStatus) {
    this.tabChanged.emit(tab);
  }

  onSearchInput(value: string) {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      const trimmed = value.trim();
      this.memberChanged.emit(trimmed === '' ? null : trimmed);
    }, SEARCH_DEBOUNCE_MS);
  }

  onRowClick(supportId: string) {
    this.rowClicked.emit(supportId);
  }

  emptyStateKey(): string | null {
    if (this.cases().length > 0) return null;
    return 'admin_support.lbl_no_cases';
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount() / this.pageSize()));
  }

  hasPrevPage(): boolean {
    return this.page() > 1;
  }

  hasNextPage(): boolean {
    return this.page() < this.totalPages();
  }

  onPrevPage() {
    if (this.hasPrevPage()) this.pageChanged.emit(this.page() - 1);
  }

  onNextPage() {
    if (this.hasNextPage()) this.pageChanged.emit(this.page() + 1);
  }

  ngOnDestroy() {
    clearTimeout(this.searchDebounceTimer);
  }
}
